/**
 * The outline level of each body paragraph, read from `word/styles.xml` and the
 * paragraphs' own properties: Word's own statement of which paragraphs are
 * headings and how deeply they nest.
 *
 * A contract does not have to type its clause numbers. `ARTICLE 1 —
 * DEFINITIONS` carries no number the text can be parsed for, but it is almost
 * always styled `Heading 1`, and that style carries `w:outlineLvl w:val="0"`.
 * That tag is the only signal that says "this is a heading" rather than
 * "this paragraph happens to start with a number", so the core's heading map
 * prefers it over anything it can infer from the text.
 *
 * A paragraph takes its outline level from its own `w:pPr/w:outlineLvl` when it
 * states one, and otherwise from its paragraph style, following `w:basedOn` up
 * the chain until a style states one. `w:outlineLvl` numbers heading levels 0
 * to 8; 9 is Word's "body text", which is the absence of a heading rather than
 * a tenth heading depth, so it is reported as null alongside the paragraphs
 * that state no level at all.
 */
import { flatOpcPartXml, parseXmlDocumentOrNull } from "./flat-opc-parts";
import { STYLES_PART_NAME, WORDPROCESSINGML_NS, elementNameWithoutPrefix } from "./ooxml-names";

/** The one part outline levels are read from, as a string, or null when the file lacks it. */
export interface OutlineLevelParts {
    stylesXml: string | null;
}

/** What a `w:style` says about outline level: its own `w:outlineLvl`, the style it is based on, and its name. */
interface StyleOutlineLevel {
    outlineLevel: number | null;
    basedOnStyleId: string | null;
    /** `w:name`, the name a table of contents lists the style by (`TableOfContentsListing`); null when the style states none. */
    styleName: string | null;
}

/**
 * `w:outlineLvl` numbers heading levels 0 through 8. The schema's tenth value,
 * 9, means "body text": the paragraph is deliberately not a heading.
 */
const BODY_TEXT_OUTLINE_LEVEL = 9;
/** A `w:basedOn` chain longer than this is a cycle in a broken file; stop rather than loop. */
const MAX_STYLE_CHAIN_DEPTH = 20;

const elementChildren = (parent: Element, localName: string): Element[] =>
    Array.from(parent.childNodes).filter((node): node is Element => node.nodeType === 1 && elementNameWithoutPrefix(node as Element) === localName);

const firstElementChild = (parent: Element, localName: string): Element | null => elementChildren(parent, localName)[0] ?? null;

const attributeValue = (element: Element | null, attributeName: string): string | null =>
    element === null ? null : (element.getAttributeNS(WORDPROCESSINGML_NS, attributeName) ?? element.getAttribute(`w:${attributeName}`));

/**
 * `w:val` of the named child, parsed as an integer.
 *
 * CASES
 *   - the child is absent, or its value is not a number -> null
 *   - otherwise                                         -> the integer
 */
const integerChildValue = (parent: Element, localName: string): number | null => {
    const value = attributeValue(firstElementChild(parent, localName), "val");
    if (value === null) return null;
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
};

export class OoxmlOutlineLevels {
    private constructor(private readonly stylesById: ReadonlyMap<string, StyleOutlineLevel>) {}

    /** A document with no styles part, or one whose part was not available to the caller: every level is null. */
    static none(): OoxmlOutlineLevels {
        return new OoxmlOutlineLevels(new Map());
    }

    /**
     * Reads the styles part of an unpacked `.docx`.
     *
     * CASES
     *   - no styles part, or one that does not parse -> no style states a level; a
     *                                                  paragraph's own `w:outlineLvl`
     *                                                  is still read
     */
    static fromParts(parts: OutlineLevelParts): OoxmlOutlineLevels {
        const stylesDocument = parts.stylesXml === null ? null : parseXmlDocumentOrNull(parts.stylesXml);
        if (stylesDocument === null) return OoxmlOutlineLevels.none();
        return new OoxmlOutlineLevels(OoxmlOutlineLevels.stylesOf(stylesDocument));
    }

    /**
     * Reads the styles part out of a flat OPC `pkg:package`; a bare `w:document`
     * or a paragraph fragment has no parts and so no styles.
     */
    static fromPackage(packageXml: string): OoxmlOutlineLevels {
        const packageDocument = parseXmlDocumentOrNull(packageXml);
        /**
         * WHEN the markup does not parse as a whole document
         *   CONSEQUENCE
         *     it is a fragment; fragments carry no styles part.
         */
        if (packageDocument === null) return OoxmlOutlineLevels.none();
        return OoxmlOutlineLevels.fromParts({ stylesXml: flatOpcPartXml(packageDocument, STYLES_PART_NAME) });
    }

    /**
     * The name of the style a paragraph names, as `w:name` spells it.
     *
     * NULL
     *   The paragraph names no style, names one the styles part does not define, or its
     *   style states no name.
     *
     * EXAMPLE
     *   a paragraph styled `Schedule`, whose style is named "Schedule" -> "Schedule"
     *   a paragraph with no `w:pStyle`                               -> null
     */
    styleNameOf(paragraph: Element): string | null {
        const paragraphProperties = firstElementChild(paragraph, "pPr");
        /** NULL: no properties block, so no style named. */
        if (paragraphProperties === null) return null;
        const styleId = attributeValue(firstElementChild(paragraphProperties, "pStyle"), "val");
        /** NULL: no style named, or one the styles part does not define. */
        if (styleId === null) return null;
        return this.stylesById.get(styleId)?.styleName ?? null;
    }

    /**
     * The outline level of each of `paragraphs`, in their order, or null for a
     * paragraph that is not a heading.
     *
     * EXAMPLE
     *   a paragraph styled `Heading1`, whose style carries `w:outlineLvl w:val="0"` -> 0
     *   a paragraph styled `Heading2`, based on `Heading1`, stating `w:val="1"`     -> 1
     *   a paragraph styled `Normal`, whose style states `w:val="9"`                 -> null
     *   a paragraph with no style and no `w:outlineLvl`                             -> null
     */
    levelsByParagraph(paragraphs: readonly Element[]): (number | null)[] {
        return paragraphs.map((paragraph) => this.levelOf(paragraph));
    }

    /**
     * The outline level in force for one paragraph: its own before its style's.
     *
     * NULL
     *   Neither the paragraph nor any style in its `w:basedOn` chain states a
     *   heading level, or the level in force is `BODY_TEXT_OUTLINE_LEVEL`, which
     *   states that the paragraph is body text.
     */
    private levelOf(paragraph: Element): number | null {
        const paragraphProperties = firstElementChild(paragraph, "pPr");
        /**
         * WHEN the paragraph carries no properties block
         *   CONSEQUENCE
         *     it names no style and states no level of its own; it is not a heading.
         */
        if (paragraphProperties === null) return null;
        const ownOutlineLevel = integerChildValue(paragraphProperties, "outlineLvl");
        /**
         * WHEN the paragraph states its own outline level
         *   CONSEQUENCE
         *     it wins over the style it would otherwise inherit, the way direct
         *     formatting wins over a style everywhere else in the format.
         */
        if (ownOutlineLevel !== null) return OoxmlOutlineLevels.headingLevelOrNull(ownOutlineLevel);
        const styleId = attributeValue(firstElementChild(paragraphProperties, "pStyle"), "val");
        return OoxmlOutlineLevels.headingLevelOrNull(this.styleChainOutlineLevel(styleId));
    }

    /**
     * The first outline level stated by `styleId` or by a style it is based on.
     *
     * EXAMPLE
     *   Heading2 states nothing, is based on Heading1, which states 0 -> 0
     *
     * NULL
     *   No style in the chain states a level, the chain names a style the file does
     *   not define, or the chain is longer than `MAX_STYLE_CHAIN_DEPTH` and so is a
     *   cycle in a broken file.
     */
    private styleChainOutlineLevel(styleId: string | null): number | null {
        for (let currentStyleId = styleId, depth = 0; currentStyleId !== null && depth < MAX_STYLE_CHAIN_DEPTH; depth += 1) {
            const style = this.stylesById.get(currentStyleId);
            /**
             * WHEN the chain names a style the styles part does not define
             *   CONSEQUENCE
             *     nothing further can be inherited; the paragraph is not a heading.
             */
            if (style === undefined) return null;
            if (style.outlineLevel !== null) return style.outlineLevel;
            currentStyleId = style.basedOnStyleId;
        }
        return null;
    }

    /**
     * An outline level as a heading depth, or null when it is not one.
     *
     * CASES
     *   - null, so no level is stated       -> null
     *   - BODY_TEXT_OUTLINE_LEVEL, or above -> null: Word says this is body text
     *   - below zero, which the schema
     *     does not allow                    -> null, rather than a negative depth
     *   - 0 through 8                       -> the level itself
     */
    private static headingLevelOrNull(outlineLevel: number | null): number | null {
        if (outlineLevel === null) return null;
        if (outlineLevel < 0 || outlineLevel >= BODY_TEXT_OUTLINE_LEVEL) return null;
        return outlineLevel;
    }

    /** Every `w:style` in the part, by its id: the outline level it states and the style it is based on. */
    private static stylesOf(stylesDocument: Document): Map<string, StyleOutlineLevel> {
        const styles = new Map<string, StyleOutlineLevel>();
        for (const styleElement of Array.from(stylesDocument.getElementsByTagNameNS(WORDPROCESSINGML_NS, "style"))) {
            const styleId = attributeValue(styleElement, "styleId");
            if (styleId === null) continue;
            const paragraphProperties = firstElementChild(styleElement, "pPr");
            styles.set(styleId, {
                outlineLevel: paragraphProperties === null ? null : integerChildValue(paragraphProperties, "outlineLvl"),
                basedOnStyleId: attributeValue(firstElementChild(styleElement, "basedOn"), "val"),
                styleName: attributeValue(firstElementChild(styleElement, "name"), "val"),
            });
        }
        return styles;
    }
}
